import React, { useState } from 'react';
import {
  Heart,
  Repeat2,
  MessageCircle,
  Bookmark,
  Share2,
  MoreHorizontal,
  Trash2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import {
  getAvatarUrl,
  formatRelativeTime,
  formatCount,
} from '../../utils/helpers';

import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';

export default function PostCard({
  post,
  onLike,
  onRepost,
  onBookmark,
  onDelete,
}) {
  const [showMenu, setShowMenu] = useState(false);

  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();

  const myId = user?._id || user?.id;

  const authorId =
    post.author?._id ||
    post.author?.id;

  const isOwn = authorId === myId;

  const postId = post._id || post.id;

  const share = async () => {
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}/post/${postId}`
      );

      toast.success('Link copied!');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const openProfile = () => {
    if (authorId) {
      navigate(`/profile/${authorId}`);
    }
  };

  return (
    <article className="post-card">

      {/* =====================================================
          POST HEADER
      ===================================================== */}

      <div className="post-header">

        <button
          type="button"
          className="post-avatar-button"
          onClick={openProfile}
          aria-label={`Open ${post.author?.displayName || 'user'} profile`}
        >
          <img
            src={getAvatarUrl(post.author)}
            alt={post.author?.displayName || 'User'}
            className="post-avatar"
          />
        </button>

        <div className="post-author-info">

          <div className="post-author-top">

            <button
              type="button"
              className="post-author-name"
              onClick={openProfile}
            >
              {post.author?.displayName || 'Nexa User'}
            </button>

            {post.author?.isVerified && (
              <span
                className="verified-badge"
                title="Verified"
              >
                ✓
              </span>
            )}

            <span className="post-author-handle">
              @{post.author?.username || 'user'}
            </span>

            <span className="post-dot">·</span>

            <span className="post-time">
              {formatRelativeTime(post.createdAt)}
            </span>

          </div>

        </div>

        {/* ===================================================
            MENU
        =================================================== */}

        <div className="post-menu-wrapper">

          <button
            type="button"
            className="post-more-button"
            onClick={() => setShowMenu((value) => !value)}
            aria-label="Post options"
          >
            <MoreHorizontal size={18} />
          </button>

          {showMenu && (
            <>
              <div className="post-menu">

                <button
                  type="button"
                  onClick={() => {
                    share();
                    setShowMenu(false);
                  }}
                >
                  <Share2 size={14} />
                  Copy link
                </button>

                <button
                  type="button"
                  onClick={() => {
                    toast.info('Not interested');
                    setShowMenu(false);
                  }}
                >
                  🚫
                  Not interested
                </button>

                {isOwn && (
                  <button
                    type="button"
                    className="danger"
                    onClick={() => {
                      onDelete?.(postId);
                      setShowMenu(false);
                    }}
                  >
                    <Trash2 size={14} />
                    Delete post
                  </button>
                )}

              </div>

              <button
                type="button"
                className="post-menu-backdrop"
                aria-label="Close menu"
                onClick={() => setShowMenu(false)}
              />
            </>
          )}

        </div>

      </div>


      {/* =====================================================
          POST CONTENT
      ===================================================== */}

      <div className="post-body">

        {post.content && (
          <p className="post-content">
            {post.content}
          </p>
        )}


        {/* ===================================================
            POST MEDIA
        =================================================== */}

        {Array.isArray(post.mediaUrls) &&
          post.mediaUrls.length > 0 && (

            <div
              className={`post-media-grid grid-${Math.min(
                post.mediaUrls.length,
                4
              )}`}
            >

              {post.mediaUrls
                .slice(0, 4)
                .map((media, index) => {

                  const imageUrl =
                    typeof media === 'string'
                      ? media
                      : media?.url;

                  if (!imageUrl) return null;

                  return (
                    <button
                      key={`${imageUrl}-${index}`}
                      type="button"
                      className="post-media-button"
                      onClick={() =>
                        window.open(
                          imageUrl,
                          '_blank',
                          'noopener,noreferrer'
                        )
                      }
                    >
                      <img
                        src={imageUrl}
                        alt={`Post media ${index + 1}`}
                        className="post-media-img"
                      />
                    </button>
                  );
                })}

            </div>
          )}


        {/* ===================================================
            HASHTAGS
        =================================================== */}

        {Array.isArray(post.hashtags) &&
          post.hashtags.length > 0 && (

            <div className="post-tags">

              {post.hashtags.map((tag) => (
                <span
                  key={tag}
                  className="post-tag"
                >
                  #{tag}
                </span>
              ))}

            </div>
          )}

      </div>


      {/* =====================================================
          ACTIONS
      ===================================================== */}

      <div className="post-actions">

        <button
          type="button"
          className={`post-action-btn ${
            post.isLiked ? 'liked' : ''
          }`}
          onClick={() => onLike?.(postId)}
        >
          <Heart
            size={17}
            fill={post.isLiked ? 'currentColor' : 'none'}
          />

          <span>
            {formatCount(post.likeCount || 0)}
          </span>
        </button>


        <button
          type="button"
          className="post-action-btn"
          onClick={() => navigate(`/feed?post=${postId}`)}
        >
          <MessageCircle size={17} />

          <span>
            {formatCount(post.replyCount || 0)}
          </span>
        </button>


        <button
          type="button"
          className={`post-action-btn ${
            post.isReposted ? 'reposted' : ''
          }`}
          onClick={() => onRepost?.(postId)}
        >
          <Repeat2 size={17} />

          <span>
            {formatCount(post.repostCount || 0)}
          </span>
        </button>


        <button
          type="button"
          className={`post-action-btn ${
            post.isBookmarked ? 'bookmarked' : ''
          }`}
          onClick={() => onBookmark?.(postId)}
          aria-label="Bookmark post"
        >
          <Bookmark
            size={17}
            fill={
              post.isBookmarked
                ? 'currentColor'
                : 'none'
            }
          />
        </button>


        <button
          type="button"
          className="post-action-btn"
          onClick={share}
          aria-label="Share post"
        >
          <Share2 size={17} />
        </button>

      </div>

    </article>
  );
}